/**
 * Polygon-edge wireframe overlay for scene objects.
 *
 * Renders polygon boundary edges as LineSegments — no triangle diagonals.
 * Drop-in replacement for Three.js's `wireframe: true` material approach.
 */

import { useThree } from "@react-three/fiber";
import { useLayoutEffect, useMemo } from "react";
import * as THREE from "three/webgpu";
import { useResolvedLiveEditGeometry } from "../app/useLiveEditPreview.ts";
import type { PolygonTopology } from "../modeling/types.ts";
import { EDGE_WIRE } from "../modeling/types.ts";
import { addComponentUpdateRanges } from "./bufferUpdateRanges.ts";
import { useDispose } from "./hooks/useDispose.ts";

/**
 * LineSegments render thinner than Three.js's triangle-edge wireframe.
 * This multiplier compensates so that callers can specify opacity as if
 * using the built-in wireframe material.
 */
const LINE_OPACITY_SCALE = 0.75;

/**
 * Build a LineSegments-compatible BufferGeometry from polygon topology.
 * Uses the explicit edges array directly — no need to derive edge pairs.
 */
export function buildPolyEdgeGeometry(
  vertices: Float32Array,
  topology: PolygonTopology,
): THREE.BufferGeometry {
  // topology.edges is already flat [v0, v1, v0, v1, ...] — use it directly
  const geo = new THREE.BufferGeometry();
  geo.setAttribute(
    "position",
    new THREE.BufferAttribute(new Float32Array(vertices), 3),
  );
  geo.setIndex(new THREE.BufferAttribute(new Uint32Array(topology.edges), 1));
  return geo;
}

/** Props for the PolyWireframe component. */
interface PolyWireframeProps {
  objectId?: string | null;
  vertices: Float32Array;
  topology: PolygonTopology;
  color: string;
  opacity: number;
  depthTest?: boolean;
  renderOrder?: number;
  /** When provided, edges belonging only to hidden faces are filtered out. */
  hiddenFaces?: Set<number>;
  /** When provided, render only the listed edge indices. */
  onlyEdges?: ReadonlySet<number>;
  /** Exact vertices touched by the latest live preview tick. */
  dirtyVertices?: Uint32Array | null;
}

/**
 * Renders polygon boundary edges as a LineSegments overlay.
 */
export function PolyWireframe({
  objectId = null,
  vertices,
  topology,
  color,
  opacity,
  depthTest = true,
  renderOrder = 1,
  hiddenFaces,
  onlyEdges,
  dirtyVertices = null,
}: PolyWireframeProps) {
  const { invalidate } = useThree();
  const preview = useResolvedLiveEditGeometry(
    objectId,
    vertices,
    dirtyVertices,
  );
  const previewVertices = preview.vertices;
  const previewDirtyVertices = preview.dirtyVertices;
  const previewDirtyPositions = preview.dirtyVertexPositions;
  // Build edge index buffer only when topology changes (not on vertex moves).
  // When hiddenFaces is provided, filter out edges that belong only to hidden faces.
  const geometry = useMemo(() => {
    if (
      (!hiddenFaces || hiddenFaces.size === 0) &&
      (!onlyEdges || onlyEdges.size === 0)
    ) {
      return buildPolyEdgeGeometry(previewVertices, topology);
    }
    const hidden = hiddenFaces ?? new Set<number>();
    const { edges, cornerEdges, edgeFlags } = topology;
    const edgeCount = edges.length / 2;
    // Build edge→face adjacency from cornerEdges
    const edgeFaces = new Map<number, number[]>();
    const faceCount = topology.offsets.length - 1;
    for (let fi = 0; fi < faceCount; fi++) {
      const start = topology.offsets[fi];
      const size = topology.offsets[fi + 1] - start;
      for (let j = 0; j < size; j++) {
        const ei = cornerEdges[start + j];
        let list = edgeFaces.get(ei);
        if (!list) {
          list = [];
          edgeFaces.set(ei, list);
        }
        list.push(fi);
      }
    }
    // Filter: keep edges where at least one adjacent face is visible (or wire edges)
    const kept: number[] = [];
    for (let ei = 0; ei < edgeCount; ei++) {
      if (onlyEdges && !onlyEdges.has(ei)) continue;
      if (edgeFlags[ei] & EDGE_WIRE) {
        kept.push(edges[ei * 2], edges[ei * 2 + 1]);
        continue;
      }
      const faces = edgeFaces.get(ei);
      if (!faces || !faces.every((fi) => hidden.has(fi))) {
        kept.push(edges[ei * 2], edges[ei * 2 + 1]);
      }
    }
    const edgeIndices = new Uint32Array(kept);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(previewVertices), 3),
    );
    geo.setIndex(new THREE.BufferAttribute(edgeIndices, 1));
    return geo;
  }, [topology, hiddenFaces, onlyEdges, previewVertices.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dispose the edge geometry when it is replaced (topology/hidden-face change)
  // or when this component unmounts.  Without this, each topology-changing edit
  // operation (extrude, subdivide, delete face) leaks a BufferGeometry on the GPU.
  useDispose(geometry);

  // Update vertex positions in-place when they change (vertex move without topology change).
  // When the vertex count changes the useMemo rebuilds geometry entirely, so we only
  // reach here for same-count updates.
  useLayoutEffect(() => {
    const posAttr = geometry.getAttribute("position") as THREE.BufferAttribute;
    if (posAttr.array.length === previewVertices.length) {
      const posArr = posAttr.array as Float32Array;
      if (previewDirtyVertices && previewDirtyVertices.length > 0) {
        for (let i = 0; i < previewDirtyVertices.length; i++) {
          const vi = previewDirtyVertices[i]!;
          const src = previewDirtyPositions ? i * 3 : vi * 3;
          const base = vi * 3;
          posArr[base] = previewDirtyPositions
            ? previewDirtyPositions[src]!
            : previewVertices[base]!;
          posArr[base + 1] = previewDirtyPositions
            ? previewDirtyPositions[src + 1]!
            : previewVertices[base + 1]!;
          posArr[base + 2] = previewDirtyPositions
            ? previewDirtyPositions[src + 2]!
            : previewVertices[base + 2]!;
        }
        addComponentUpdateRanges(posAttr, previewDirtyVertices, 3);
      } else {
        posArr.set(previewVertices);
        posAttr.updateRanges.length = 0;
      }
      posAttr.needsUpdate = true;
    } else {
      // Vertex count changed independently of topology — replace the attribute directly.
      geometry.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(previewVertices), 3),
      );
    }
    invalidate();
  }, [
    previewVertices,
    geometry,
    previewDirtyVertices,
    previewDirtyPositions,
    invalidate,
  ]);

  return (
    <lineSegments geometry={geometry} renderOrder={renderOrder}>
      <lineBasicMaterial
        color={color}
        transparent
        opacity={Math.min(1, opacity * LINE_OPACITY_SCALE)}
        depthTest={depthTest}
        depthWrite={false}
      />
    </lineSegments>
  );
}
